import { useState } from 'react'
import type { ReactElement } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { isRoomCode, normalizeRoomCode } from '@games/net'
import { ConnectionBadge } from '../components/ConnectionBadge'
import { Panel } from '../components/Panel'
import { RoomScreen } from '../room/RoomScreen'
import { useRoomSession } from '../room/useRoomSession'

const INVALID_ROOM_HINT = '房间号格式不对，请检查后重试。'

/**
 * `/fogchess` 页面：无 `room` 时提供创建/加入；有 `room` 时自动作为加入方连接
 * （由 `useRoomSession` 用 ref 保证只触发一次，StrictMode 下同样只触发一次）。
 */
export function FogChessView(): ReactElement {
  const [searchParams] = useSearchParams()
  const roomFromUrl = searchParams.get('room')
  const session = useRoomSession(roomFromUrl)
  const { state } = session

  const [codeInput, setCodeInput] = useState(() => normalizeRoomCode(roomFromUrl ?? ''))
  const [inputError, setInputError] = useState<string | null>(null)

  const busy = state.status === 'signaling' || state.status === 'connecting'
  const inRoom = state.phase !== 'idle'
  // 自动加入失败（房间不存在/号不对/房间已满）时退回表单，给出错误与手动重试入口
  const joinFailed = state.phase === 'joining' && state.game === null && state.error !== null

  const ensureIdle = () => {
    if (state.phase !== 'idle' || state.status !== 'idle' || state.error !== null) {
      session.cancel()
    }
  }

  const handleHost = () => {
    setInputError(null)
    ensureIdle()
    session.host()
  }

  const handleJoin = () => {
    const code = normalizeRoomCode(codeInput)
    if (!isRoomCode(code)) {
      setInputError(INVALID_ROOM_HINT)
      return
    }
    setInputError(null)
    ensureIdle()
    session.join(code)
  }

  if (inRoom && !joinFailed) {
    return <RoomScreen session={session} />
  }

  const errorText = inputError ?? state.error

  return (
    <div className="mx-auto flex h-full w-full max-w-xl min-h-0 flex-col overflow-y-auto px-3 pb-6">
      <Panel title="战争迷雾象棋">
        <p className="text-sm text-white/70">
          规则和普通象棋一样，只能看到己方棋子能走到的格子，其余都是战争迷雾；吃掉对方将/帅即胜。
          房主固定执红先行。
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="rounded-lg border border-accent/60 bg-accent/15 px-4 py-2 text-sm text-accent hover:bg-accent/25 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={busy}
            onClick={handleHost}
          >
            创建对局
          </button>
          {state.status !== 'idle' && !joinFailed ? (
            <ConnectionBadge status={state.status} error={state.error} />
          ) : null}
        </div>
      </Panel>

      <Panel title="加入对局">
        <div className="flex flex-wrap items-stretch gap-2">
          <input
            // text-base + min-h-11：见 ChatPanel 的说明（防 iOS 聚焦缩放 + 触控尺寸）
            className="min-h-11 min-w-0 flex-1 rounded-lg border border-white/20 bg-black/30 px-3 py-2 font-mono text-base tracking-[2px] text-white uppercase placeholder:tracking-normal placeholder:text-white/40 md:min-h-0 md:text-sm"
            placeholder="输入房间号"
            autoComplete="off"
            aria-label="房间号"
            value={codeInput}
            onChange={(event) => setCodeInput(event.target.value.toUpperCase())}
          />
          <button
            type="button"
            className="shrink-0 rounded-lg border border-white/25 px-4 py-2 text-sm text-white/90 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={busy}
            onClick={handleJoin}
          >
            加入对局
          </button>
        </div>
        {errorText ? <p className="mt-2 text-sm text-danger">{errorText}</p> : null}
      </Panel>

      <div className="px-1 pt-3">
        <Link to="/" className="text-sm text-white/70 no-underline hover:text-accent">
          ← 返回大厅
        </Link>
      </div>
    </div>
  )
}
